/** Public-site visit notes for Postlude. Passwords are never sent. */

const SESSION_KEY = 'nextcompli.postlude.public';

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    return configured.replace(/\/$/, '').replace(/localhost/i, '127.0.0.1');
  }
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'http://127.0.0.1:8000';
  }
  return `https://${host.replace(/^www\./, '')}`;
};

const signedIn = (): boolean => {
  try {
    return Boolean(window.localStorage.getItem('nextcompli.auth.token') || window.localStorage.getItem('nextcompli.authenticated') === 'true');
  } catch {
    return false;
  }
};

export function postludeSessionId(): string {
  if (typeof window === 'undefined') {
    return 'server-session';
  }
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY) || '';
    if (existing.length >= 8) {
      return existing;
    }
    const next = window.crypto?.randomUUID?.() || `visit-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    window.sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return `visit-${Date.now().toString(36)}`;
  }
}

let quietTimer = 0;
let abandonTimer = 0;

export function notePostludeVisit(
  kind: 'page' | 'login_failed' | 'login_ok' | 'signup' | 'chat' | 'search' | 'leave',
  extra: { path?: string; note?: string } = {},
): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (process.env.NODE_ENV === 'test' && process.env.REACT_APP_POSTLUDE_VISITS !== '1') {
    return;
  }
  if (signedIn()) {
    return;
  }
  const path = extra.path || window.location.pathname;
  const body = JSON.stringify({
    session_id: postludeSessionId(),
    kind,
    path,
    note: (extra.note || '').slice(0, 240),
  });
  const url = `${inferGatewayBaseUrl()}/api/v1/postlude/public-visits`;
  try {
    if (navigator.sendBeacon && (kind === 'leave' || kind === 'page')) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) {
        if (kind === 'leave') {
          window.clearTimeout(quietTimer);
          window.clearTimeout(abandonTimer);
          try {
            window.sessionStorage.removeItem(SESSION_KEY);
          } catch {
            /* ignore */
          }
        }
        return;
      }
    }
  } catch {
    /* fetch below */
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
  if (kind === 'chat' || kind === 'search') {
    window.clearTimeout(quietTimer);
    quietTimer = window.setTimeout(() => notePostludeVisit('leave'), 60_000);
  }
  if (kind === 'leave') {
    window.clearTimeout(quietTimer);
    window.clearTimeout(abandonTimer);
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  }
}

export function installPostludeVisit(): void {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') {
    return;
  }
  window.addEventListener('pagehide', () => notePostludeVisit('leave'));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      window.clearTimeout(abandonTimer);
      abandonTimer = window.setTimeout(() => notePostludeVisit('leave'), 5 * 60 * 1000);
    } else {
      window.clearTimeout(abandonTimer);
    }
  });
}
